import React, { memo, useEffect, useState } from 'react';

import { Badge, Divider, Group, Stack, Text } from '@mantine/core';
import { WorkoutMovementWithScoring } from 'components/Workout/logging/types';
import {
  DistanceUnit,
  MovementResult,
  ScoringType,
  ScoringTypeInput,
  WeightUnit,
} from 'components/Workout/ScoringTypes';

import SaveStatusIndicator, { SaveStatus } from './SaveStatusIndicator';

const SAVED_INDICATOR_DURATION_MS = 2000;

const SCORING_TYPE_LABELS: Record<ScoringType, string> = {
  [ScoringType.TIME]: 'Time',
  [ScoringType.CALORIES]: 'Calories',
  [ScoringType.DISTANCE]: 'Distance',
  [ScoringType.REPS]: 'Reps',
  [ScoringType.ROUNDS]: 'Rounds',
  [ScoringType.ROUNDS_REPS]: 'Rounds & Reps',
  [ScoringType.WEIGHT]: 'Weight',
};

interface WorkoutMovementInputProps {
  movement: WorkoutMovementWithScoring;
  value: MovementResult | undefined;
  onChange: (movementId: string, result: MovementResult) => void;
  onCommit: (movementId: string, result: MovementResult) => Promise<void>;
  showDivider?: boolean;
}

const WorkoutMovementInput = ({
  movement,
  value,
  onChange,
  onCommit,
  showDivider = false,
}: WorkoutMovementInputProps): JSX.Element => {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const movementId = movement.workoutSectionMovementId;
  const movementName = movement.workoutMovement?.name ?? movement.workoutMovementName;
  const { scoringType } = movement;
  const weightUnit: WeightUnit = movement.weightUnit === 'kg' ? 'kg' : 'lbs';
  const distanceUnit: DistanceUnit =
    movement.distanceUnit === 'km' ? 'km' : movement.distanceUnit === 'mi' ? 'mi' : 'm';
  const targetReps = movement.scoreSets.map(s => s.targetReps);

  const handleChange = (result: MovementResult): void => {
    if (JSON.stringify(result) === JSON.stringify(value)) return;
    setSaveStatus('dirty');
    onChange(movementId, result);
  };

  // Auto-hide the saved indicator after a short window. Effect cleanup clears
  // the timer when status changes (e.g. user re-edits) or the component
  // unmounts, so the orphan timer can't trample a later 'dirty'/'saving'.
  useEffect(() => {
    if (saveStatus !== 'saved') return;
    const id = window.setTimeout(() => setSaveStatus('idle'), SAVED_INDICATOR_DURATION_MS);
    return () => window.clearTimeout(id);
  }, [saveStatus]);

  return (
    <Stack spacing='xs'>
      {showDivider && <Divider />}
      <Group spacing='xs' align='center' noWrap>
        <Text weight={700} size='md'>
          {movementName}
        </Text>
        <Badge variant='light' color='gray' size='sm' radius='sm' sx={{ flexShrink: 0 }}>
          {SCORING_TYPE_LABELS[scoringType]}
        </Badge>
        <SaveStatusIndicator status={saveStatus} />
      </Group>
      <div
        onBlur={async e => {
          if (e.currentTarget.contains(e.relatedTarget as Node) || !value || saveStatus !== 'dirty') return;
          setSaveStatus('saving');
          try {
            await onCommit(movementId, value);
            setSaveStatus('saved');
          } catch {
            setSaveStatus('dirty');
          }
        }}
      >
        <ScoringTypeInput
          scoringType={scoringType}
          value={value}
          onChange={handleChange}
          weightUnit={weightUnit}
          distanceUnit={distanceUnit}
          targetReps={targetReps}
        />
      </div>
    </Stack>
  );
};

export default memo(WorkoutMovementInput);
